import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCryptoHealth, type CryptoHealth, type CryptoStateAge } from '../../queries/cryptoDashboard';

const AGE_SECTIONS = [
  { title: 'Wallet age', field: 'wallets' },
  { title: 'Verification age', field: 'callbacks' },
  { title: 'Delivery age', field: 'deliveries' },
  { title: 'Withdrawal age', field: 'withdrawals' },
] as const;

const QUEUE_SECTIONS = [
  { title: 'Verification', field: 'verification' },
  { title: 'Delivery', field: 'delivery' },
  { title: 'Withdrawal', field: 'withdrawal' },
] as const;

const StateSummary = ({ title, rows }: { title: string; rows: CryptoStateAge[] }) => (
  <Paper variant='outlined' sx={{ p: 2, flex: 1 }}>
    <Typography variant='h6'>{title}</Typography>
    {!rows.length && <Typography>No records.</Typography>}
    {rows.map((row) => (
      <Typography key={row.state}>
        {row.state.replaceAll('_', ' ')}: {row.count} · Oldest created: {row.oldestCreatedAt ?? 'Unavailable'}
      </Typography>
    ))}
  </Paper>
);

// 0xProcessing's authenticated CoinInfo identifies bare USDT as the Ethereum token.
// Keep this display alias local to merchant balances; API identifiers remain untouched.
const providerBalanceLabel = (currency: string) => (currency === 'USDT' ? 'USDT (ERC20)' : currency);

const ProviderBalancesSummary = ({ balances }: { balances: CryptoHealth['providerBalances'] }) => (
  <>
    <Typography variant='h6'>Provider merchant balances</Typography>
    <Alert severity='info'>
      Balances cover the merchant, including all configured marketplaces: {balances.sharedMarketplaceIds.join(', ')}.
      They are not player balances or a payout guarantee.
    </Alert>
    <Typography>
      Source: {balances.source} · Checked: {balances.checkedAt} · Refresh after:{' '}
      {balances.expiresAt ?? 'Read incomplete'}
    </Typography>
    <Typography>
      Last successful balance refresh (since gateway start): {balances.lastSuccessfulAt ?? 'No observation'}
    </Typography>
    {balances.status === 'unavailable' && (
      <Alert severity='warning'>Provider balances unavailable; no zero balance is assumed.</Alert>
    )}
    {balances.balances?.map((balance) => (
      <Typography key={balance.currency}>
        {providerBalanceLabel(balance.currency)}: {balance.amount}
      </Typography>
    ))}
  </>
);

const QueueSummary = ({ queues }: { queues: CryptoHealth['queues'] }) => (
  <>
    <Typography variant='h6'>Queues (all gateway marketplaces)</Typography>
    {QUEUE_SECTIONS.map(({ title, field }) => {
      const queue = queues[field];
      const counts = queue?.counts
        ? Object.entries(queue.counts)
            .map(([state, count]) => `${state}: ${count}`)
            .join(', ')
        : 'Counts unavailable';

      return (
        <Typography key={field}>
          {title}: {queue?.status ?? 'unavailable'} · {counts}
        </Typography>
      );
    })}
  </>
);

const UnattributedCallbacksSummary = ({ data }: { data: CryptoHealth['unattributedCallbacks'] }) => {
  if (!data) {
    return (
      <Alert severity='warning'>
        Unattributed callback counts unavailable. Pending callbacks may be absent from marketplace totals.
      </Alert>
    );
  }

  return (
    <>
      <Typography variant='h6'>Callbacks without verified marketplace</Typography>
      <Alert severity={data.count > 0 ? 'warning' : 'info'}>
        These callbacks cover merchant {data.merchantId} and all its configured marketplaces:{' '}
        {data.sharedMarketplaceIds.join(', ')}. They are excluded from marketplace totals until verified.
      </Alert>
      <Typography>Unattributed callbacks: {data.count}</Typography>
      <StateSummary title='Unattributed callback age' rows={data.states} />
      <Typography>Latest merchant callback without attribution: {data.lastCallbackAt ?? 'No observation'}</Typography>
    </>
  );
};

const HealthSnapshot = ({ data }: { data: CryptoHealth }) => (
  <>
    <Typography>Checked: {data.checkedAt}</Typography>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
      {AGE_SECTIONS.map(({ title, field }) => (
        <StateSummary key={field} title={title} rows={data[field]} />
      ))}
    </Stack>
    <Typography>
      Latest callback with verified marketplace: {data.freshness.lastCallbackAt ?? 'No observation'}
    </Typography>
    <Typography>Latest authenticated verification: {data.freshness.lastVerifiedAt ?? 'No observation'}</Typography>
    <UnattributedCallbacksSummary data={data.unattributedCallbacks} />
    <ProviderBalancesSummary balances={data.providerBalances} />
    <QueueSummary queues={data.queues} />
  </>
);

const CryptoOperationalOverview = ({ marketplaceId }: { marketplaceId: string }) => {
  const query = useCryptoHealth(marketplaceId);

  return (
    <Stack spacing={2} component='section' aria-label='Operational health'>
      <Typography variant='h6'>Operational health</Typography>
      <Button onClick={() => void query.refetch()} disabled={query.isFetching}>
        Refresh operational health
      </Button>
      {query.isPending && <Typography>Loading operational health…</Typography>}
      {query.isError && (
        <Alert severity='warning'>Operational health unavailable. No balance or queue count is inferred.</Alert>
      )}
      {query.data && <HealthSnapshot data={query.data} />}
      <Link href='/super-admin/dashboard/bullboard'>Open existing Bull Board</Link>
      <Alert severity='info'>
        Inspect failed jobs and payment evidence before retrying. Reviews, conflicts and unknown payouts require
        reconciliation; this dashboard does not reset them.
      </Alert>
    </Stack>
  );
};

export default CryptoOperationalOverview;
